import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { StorageService } from '../../shared/storage/storage.service';

const LOCATION_CONTEXT_KEY = 'current_location_id';
const ADMIN_VIEW_MODE_KEY = 'is_admin_view_mode';

@Injectable({
  providedIn: 'root'
})
export class LocationContextService {
  private storage = inject(StorageService);

  private currentLocationIdSubject = new BehaviorSubject<number | null>(null);
  public currentLocationId$ = this.currentLocationIdSubject.asObservable();
  private adminViewModeSubject = new BehaviorSubject<boolean>(false);
  public isAdminViewMode$ = this.adminViewModeSubject.asObservable();

  constructor() {
    const isAdminViewMode = this.storage.get<string | boolean>(ADMIN_VIEW_MODE_KEY);
    const adminViewEnabled = isAdminViewMode === true || isAdminViewMode === 'true';

    if (adminViewEnabled) {
      this.adminViewModeSubject.next(true);
      this.currentLocationIdSubject.next(null);
      return;
    }

    // Initialize location from storage if available
    const storedLocationId = this.storage.get<number>(LOCATION_CONTEXT_KEY);
    if (storedLocationId !== null && storedLocationId !== undefined) {
      this.currentLocationIdSubject.next(storedLocationId);
    }
  }

  /**
   * Get current location ID
   */
  getCurrentLocationId(): number | null {
    return this.currentLocationIdSubject.value;
  }

  /**
   * Set current location ID
   */
  setCurrentLocationId(locationId: number): void {
    this.storage.set(LOCATION_CONTEXT_KEY, locationId);
    this.currentLocationIdSubject.next(locationId);
    this.setAdminViewMode(false);
  }

  /**
   * Clear current location ID
   */
  clearCurrentLocationId(): void {
    this.storage.remove(LOCATION_CONTEXT_KEY);
    this.currentLocationIdSubject.next(null);
  }

  /**
   * Check whether Admin View mode is enabled
   */
  isAdminViewMode(): boolean {
    return this.adminViewModeSubject.value;
  }

  /**
   * Set Admin View mode
   */
  setAdminViewMode(enabled: boolean): void {
    this.storage.set(ADMIN_VIEW_MODE_KEY, enabled.toString());
    this.adminViewModeSubject.next(enabled);

    if (enabled) {
      this.storage.remove(LOCATION_CONTEXT_KEY);
      this.currentLocationIdSubject.next(null);
    }
  }

  /**
   * Clear Admin View mode
   */
  clearAdminViewMode(): void {
    this.storage.remove(ADMIN_VIEW_MODE_KEY);
    this.adminViewModeSubject.next(false);
  }

  /**
   * Check if location context is set
   */
  hasLocationContext(): boolean {
    return this.currentLocationIdSubject.value !== null;
  }
}
